import { useMemo, useRef } from 'react';
import { DistributionGroupsForm } from '../../components/forms/distributionGroups/DistributionGroupsForm';
import { StatusMessage } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import type { DistributionGroupDetail } from '../../services/distribution/distribution.types';
import { mapGroupToFormValues } from './distributionGroupRecipients';
import type { DistributionGroupFormValues } from './distributionGroupRecipients';

interface Props {
    group: DistributionGroupDetail | null;
    submitting: boolean;
    error: string | null;
    onDismissError: () => void;
    onSave: (values: DistributionGroupFormValues) => Promise<boolean>;
    onDeleteRecipient: (recipientId: number) => Promise<void>;
    onCancel: () => void;
}

export function DistributionGroupEditor({ group, submitting, error, onDismissError, onSave, onDeleteRecipient, onCancel }: Props) {
    const contentRef = useRef<HTMLDivElement>(null);
    const initialData = useMemo(() => group ? mapGroupToFormValues(group) : undefined, [group]);

    const submit = () => {
        const form = contentRef.current?.querySelector('form');
        if (!form) throw new Error('Distribution group form is not mounted');
        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
        } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    };

    return (
        <Modal
            isOpen
            title={group ? 'Edit Distribution Group' : 'Add Distribution Group'}
            content={
                <div className="modal-form-wrapper" ref={contentRef}>
                    {error && <StatusMessage kind="error" onClose={onDismissError}>{error}</StatusMessage>}
                    <DistributionGroupsForm
                        key={group?.id ?? 'new'}
                        onSubmit={onSave}
                        onCancel={onCancel}
                        initialData={initialData}
                        isLoading={submitting}
                        groupId={group?.id}
                        onDeleteRecipient={group ? onDeleteRecipient : undefined}
                    />
                </div>
            }
            onConfirm={submit}
            onCancel={onCancel}
            okButtonLoading={submitting}
            cancelButtonLoading={submitting}
        />
    );
}
